import {
  Ban,
  CalendarCheck,
  ChevronDown,
  Clock,
  HelpCircle,
  Loader2,
  Mail,
  MessageCircle,
  MessageSquareOff,
  PencilLine,
  PhoneMissed,
  Send,
  Star,
  ThumbsDown,
  ThumbsUp,
  UserX,
} from "lucide-react";
import { useDataProvider, useNotify, useRefresh, useTranslate } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useAjustes } from "../datos/useAjustes";
import {
  conInteres,
  CANALES_ESCRITOS,
  claveAyudaResultadoPorCanal,
  claveCanal,
  claveResultadoPorCanal,
  esCanalEscrito,
  RESULTADOS_PRESENTACION,
  resultadosDeContacto,
} from "../dominio/catalogos";
import { motivosPara } from "../dominio/analitica";
import { claveEtapa, modoContacto } from "../dominio/etapas";
import { aFechaISO, sumarDias } from "../dominio/fechas";
import {
  datosQuePide,
  ErrorResultado,
  pideInteres,
  type DatosResultado,
} from "../dominio/resultado";
import type { Canal, Negocio, NivelInteres, Resultado } from "../dominio/tipos";
import { aDatetimeLocal, deDatetimeLocal } from "./formato";
import { BotonesInteres } from "./Interes";
import { ReservaConsultoria } from "./ReservaConsultoria";
import { SelectorHuecos } from "./SelectorHuecos";

const EMAIL_VALIDO = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const ICONO: Record<Resultado, typeof CalendarCheck> = {
  quiere_reunion: CalendarCheck,
  interesado: Star,
  ni_si_ni_no: HelpCircle,
  no_contesta: PhoneMissed,
  no_interesa: ThumbsDown,
  mas_adelante: Clock,
  aceptada: ThumbsUp,
  lo_pensara: Clock,
  repropuesta: PencilLine,
  no_se_presento: UserX,
  no_quiere_contacto: Ban,
};

/** Iconos de «He escrito»: mensaje enviado / sin respuesta en vez de llamada perdida. */
const ICONO_ESCRITO: Record<Resultado, typeof CalendarCheck> = {
  ...ICONO,
  ni_si_ni_no: Send,
  no_contesta: MessageSquareOff,
};

const ICONO_CANAL_ESCRITO: Record<string, typeof Mail> = {
  whatsapp: MessageCircle,
  email: Mail,
};

/** contacto: los botones mueven la tarjeta; presentacion: su resultado; anotar: sin resultado. */
type Modo = "contacto" | "presentacion" | "anotar";

export type ResultadoSheetProps = {
  negocio: Negocio;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * llamada (He llamado), visita (He visitado) o whatsapp/email (He escrito: la hoja
   * deja cambiar entre los dos). Con `presentacion`, la videollamada.
   */
  canal: Canal;
  /** Hoja «Resultado de la presentación» (Presentación agendada, Se lo tiene que pensar). */
  presentacion?: boolean;
  onGuardado?: (negocio: Negocio) => void;
};

/**
 * Hoja «He llamado / He visitado / He escrito» (SPEC.md §2 y §7): resultado + nota + fecha
 * en tres toques. Desde S8 (Robert, 02/10/2026) tiene tres modos: en las etapas del
 * comercial los resultados mueven la tarjeta; de «Preparando propuesta» en adelante y en
 * Clientes el contacto solo se anota con su siguiente paso; y la hoja «Resultado de la
 * presentación» (Aceptada, Se lo tiene que pensar, Quiere repropuesta, No le interesa). La
 * regla es aplicarResultado (dominio) = registrar_resultado (base); aquí se recogen datos.
 */
export const ResultadoSheet = ({
  negocio,
  open,
  onOpenChange,
  canal,
  presentacion = false,
  onGuardado,
}: ResultadoSheetProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const isMobile = useIsMobile();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const { ajustes } = useAjustes();

  const modo: Modo = presentacion
    ? "presentacion"
    : modoContacto(negocio) === "resultado"
      ? "contacto"
      : "anotar";

  const [canalActual, setCanalActual] = useState<Canal>(canal);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [fechaReunion, setFechaReunion] = useState("");
  // 41: con el calendario propio, la hora sale de los huecos libres (instante ISO).
  const [hueco, setHueco] = useState("");
  const queryClient = useQueryClient();
  // «Quiere reunión» sin email en la ficha: el email es obligatorio y se guarda en la ficha.
  const [email, setEmail] = useState("");
  const [fechaSiguientePaso, setFechaSiguientePaso] = useState("");
  const [siguientePaso, setSiguientePaso] = useState("");
  // 33: hora opcional del siguiente paso («HH:MM»).
  const [horaSiguientePaso, setHoraSiguientePaso] = useState("");
  const [fechaVuelta, setFechaVuelta] = useState("");
  const [noLlamar, setNoLlamar] = useState(false);
  const [texto, setTexto] = useState("");
  const [reaccion, setReaccion] = useState("");
  const [objecion, setObjecion] = useState("");
  const [motivo, setMotivo] = useState("");
  const [fechaContacto, setFechaContacto] = useState("");
  const [detalles, setDetalles] = useState(false);
  const [guardando, setGuardando] = useState(false);
  // 28d: nivel de interés; empieza con el que tiene la ficha.
  const [interes, setInteres] = useState<NivelInteres | null>(null);
  useEffect(() => {
    if (open) setInteres(negocio.interes ?? null);
  }, [open, negocio.interes]);
  // 39 (Robert, 23/10/2026): «Aceptada» pide el importe por el que se ha aceptado (propone el que
  // ya tenga o, si no, el valor estimado).
  const [importe, setImporte] = useState("");
  const importePropuesto = negocio.importe_total ?? negocio.valor_estimado;
  useEffect(() => {
    if (open)
      setImporte(importePropuesto != null ? String(importePropuesto) : "");
  }, [open, importePropuesto]);

  // Cada apertura empieza limpia, con la fecha del contacto = ahora.
  useEffect(() => {
    if (!open) return;
    const ahora = new Date();
    setCanalActual(presentacion ? "videollamada" : canal);
    setResultado(null);
    setFechaReunion("");
    setHueco("");
    // Al solo anotar no se propone fecha: así no se pisa el siguiente paso que ya hay.
    setFechaSiguientePaso(
      modo === "anotar" ? "" : aFechaISO(sumarDias(ahora, 7)),
    );
    setSiguientePaso(
      esCanalEscrito(canal) && modo === "contacto"
        ? translate("nevora.resultado.esperar_respuesta")
        : "",
    );
    setHoraSiguientePaso("");
    setFechaVuelta(aFechaISO(sumarDias(ahora, 90)));
    setNoLlamar(false);
    setTexto("");
    setReaccion("");
    setObjecion("");
    setMotivo("");
    setFechaContacto(aDatetimeLocal(ahora));
    setDetalles(false);
    setGuardando(false);
  }, [open, canal, presentacion, modo, translate]);

  const pide =
    modo === "anotar"
      ? datosQuePide(null)
      : resultado
        ? datosQuePide(resultado)
        : [];
  // Jan y Robert, 13/10/2026: «Quiere reunión» exige email si la ficha no lo tiene.
  const pideEmail =
    modo === "contacto" &&
    resultado === "quiere_reunion" &&
    !(negocio.email ?? "").trim();
  const pideImporte = modo === "presentacion" && resultado === "aceptada";
  const conMotivo = pide.includes("motivo");
  const escrito = esCanalEscrito(canalActual);
  const iconos = escrito ? ICONO_ESCRITO : ICONO;
  // 28d: el nivel, solo si la base ya lo tiene; obligatorio en «Ni sí ni no» de llamada o visita.
  const conNivel = conInteres(negocio) && modo !== "presentacion";
  const nivelObligatorio =
    conNivel && pideInteres(modo === "anotar" ? null : resultado, canalActual);
  const resultados: Resultado[] =
    modo === "presentacion"
      ? RESULTADOS_PRESENTACION
      : modo === "contacto"
        ? // «Interesado» (28c) y los de la 36, solo si la base ya los admite.
          resultadosDeContacto(negocio)
        : [];

  // «Interesado»: la fecha del siguiente paso, a 2 días, y qué toca (Jan y Robert, 13/10/2026).
  const elegir = (r: Resultado) => {
    setResultado(r);
    if (r === "interesado") {
      setFechaSiguientePaso(aFechaISO(sumarDias(new Date(), 2)));
      if (!siguientePaso.trim()) {
        setSiguientePaso(translate("nevora.resultado.paso_interesado"));
      }
    }
    // 36: «No se presentó» → llamar hoy para reagendar (lo mismo que pone la base si no hay nada).
    if (r === "no_se_presento") {
      setFechaSiguientePaso(aFechaISO(new Date()));
      setSiguientePaso(translate("nevora.resultado.paso_no_show"));
    }
  };

  const guardar = async () => {
    if (modo !== "anotar" && !resultado) {
      notify("nevora.resultado.errores.falta_resultado", { type: "warning" });
      return;
    }
    if (pideEmail && !EMAIL_VALIDO.test(email.trim())) {
      notify("nevora.resultado.errores.falta_email", { type: "warning" });
      return;
    }
    if (nivelObligatorio && !interes) {
      notify("nevora.resultado.errores.falta_interes", { type: "warning" });
      return;
    }
    const conHuecos =
      ajustes.agenda.propia &&
      modo === "contacto" &&
      resultado === "quiere_reunion";
    if (conHuecos && !hueco) {
      notify("nevora.agenda_propia.elige_hora", { type: "warning" });
      return;
    }
    const importeAceptado = Number(importe.replace(",", "."));
    if (pideImporte && !(importeAceptado > 0)) {
      notify("nevora.resultado.errores.falta_importe", { type: "warning" });
      return;
    }
    const datos: DatosResultado = {
      fecha: deDatetimeLocal(fechaContacto),
      texto,
      reaccion,
      // «No le interesa» y «Más adelante» (47): la objeción es el motivo elegido de la lista de
      // Ajustes que toca según el momento (llamada, tras la consultoría o tras la propuesta).
      objecion: conMotivo ? motivo : objecion,
      fechaReunion: deDatetimeLocal(fechaReunion),
      fechaSiguientePaso: fechaSiguientePaso || undefined,
      horaSiguientePaso:
        "hora_siguiente_paso" in negocio
          ? horaSiguientePaso || null
          : undefined,
      siguientePaso,
      fechaVuelta: fechaVuelta || undefined,
      noLlamar,
      interes: conNivel && resultado !== "interesado" ? interes : undefined,
    };
    setGuardando(true);
    try {
      let conEmail = negocio;
      if (pideEmail) {
        const { data } = await dataProvider.update<Negocio>("negocios", {
          id: negocio.negocio_id,
          data: { email: email.trim(), proyecto_id: negocio.proyecto_id },
          previousData: negocio,
        });
        conEmail = { ...negocio, email: data?.email ?? email.trim() };
      }
      // 39: la base no deja pasar a «Aceptada» sin importe; se guarda antes del resultado.
      if (pideImporte) {
        await dataProvider.ponerImporteAceptado(
          negocio.proyecto_id,
          importeAceptado,
        );
      }
      if (conHuecos) {
        // 41: la base elige quién la hace y registra «Quiere reunión» con estos datos.
        await dataProvider.agendarConsultoria({
          negocio: conEmail,
          canal: canalActual,
          inicio: hueco,
          datos,
          ajustes,
        });
        await queryClient.invalidateQueries({ queryKey: ["nevora", "huecos"] });
        notify("nevora.agenda_propia.agendada", { type: "success" });
        refresh();
        onGuardado?.({
          ...conEmail,
          etapa: "consultoria_agendada",
          fecha_reunion: hueco,
        });
        onOpenChange(false);
        return;
      }
      const { negocio: actualizado, efecto } =
        await dataProvider.registrarResultado({
          negocio: conEmail,
          canal: canalActual,
          resultado: modo === "anotar" ? null : resultado,
          datos,
          ajustes,
        });
      const etapaFinal = efecto.cambios.etapa ?? negocio.etapa;
      notify("nevora.resultado.guardado", {
        type: "success",
        messageArgs: { etapa: translate(claveEtapa(etapaFinal)) },
      });
      if (efecto.aparcadaPorIntentos) {
        notify("nevora.resultado.aparcada", {
          type: "warning",
          messageArgs: { intentos: efecto.cambios.intentos },
        });
      }
      refresh();
      onGuardado?.(actualizado);
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ErrorResultado) {
        notify(error.clave, { type: "warning" });
      } else if (
        pideEmail &&
        error instanceof Error &&
        /ya está en el CRM/.test(error.message)
      ) {
        notify("nevora.resultado.errores.email_repetido", { type: "warning" });
      } else {
        notify(
          error instanceof Error ? error.message : "ra.notification.http_error",
          { type: "error" },
        );
      }
    } finally {
      setGuardando(false);
    }
  };

  const titulo =
    modo === "presentacion"
      ? "nevora.resultado.titulo_presentacion"
      : modo === "anotar"
        ? "nevora.resultado.titulo_anotar"
        : "nevora.resultado.titulo";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className={cn(
          "flex flex-col p-0",
          isMobile ? "h-[92dvh] rounded-t-2xl" : "w-full sm:max-w-md",
        )}
      >
        <SheetHeader className="border-b px-4 py-3">
          <SheetTitle className="titulo-seccion">
            {translate(titulo)}
          </SheetTitle>
          <SheetDescription className="truncate">
            {negocio.nombre} · {translate(claveCanal(canalActual))}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          {modo === "anotar" ? (
            <p className="text-sm text-muted-foreground">
              {translate("nevora.resultado.ayuda_anotar")}
            </p>
          ) : null}

          {escrito && modo !== "presentacion" ? (
            <fieldset className="space-y-2">
              <legend className="etiqueta mb-2">
                {translate("nevora.resultado.canal_escrito")}
              </legend>
              <div className="grid grid-cols-2 gap-2">
                {CANALES_ESCRITOS.map((c) => {
                  const Icono = ICONO_CANAL_ESCRITO[c];
                  const activo = canalActual === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCanalActual(c)}
                      aria-pressed={activo}
                      className={cn(
                        "flex items-center justify-center gap-2 rounded-lg border px-4 py-3 font-medium transition-colors",
                        activo
                          ? "border-foreground bg-accent"
                          : "border-border bg-card hover:bg-muted",
                      )}
                    >
                      <Icono className="size-5 shrink-0" />
                      {translate(claveCanal(c))}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          ) : null}

          {resultados.length ? (
            <fieldset className="space-y-2">
              <legend className="etiqueta mb-2">
                {translate("nevora.resultado.resultado")}
              </legend>
              {resultados.map((r) => {
                const Icono = iconos[r];
                const activo = resultado === r;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => elegir(r)}
                    aria-pressed={activo}
                    className={cn(
                      "w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors",
                      activo
                        ? "border-foreground bg-accent"
                        : "border-border bg-card hover:bg-muted",
                    )}
                  >
                    <Icono className="size-5 shrink-0" />
                    <span className="flex-1">
                      <span className="block font-medium">
                        {translate(claveResultadoPorCanal(canalActual, r))}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {translate(claveAyudaResultadoPorCanal(canalActual, r))}
                      </span>
                    </span>
                  </button>
                );
              })}
            </fieldset>
          ) : null}

          {conNivel && (modo === "anotar" || resultado) ? (
            resultado === "interesado" ? (
              <p
                className="text-sm text-muted-foreground"
                data-testid="interes-auto"
              >
                {translate("nevora.interes.caliente_auto")}
              </p>
            ) : (
              <fieldset className="space-y-2">
                <legend className="etiqueta mb-2">
                  {translate(
                    nivelObligatorio
                      ? "nevora.interes.titulo"
                      : "nevora.interes.opcional",
                  )}
                </legend>
                <BotonesInteres valor={interes} cambiar={setInteres} />
              </fieldset>
            )
          ) : null}

          {pideImporte ? (
            <div className="space-y-2">
              <Label htmlFor="importe_aceptado">
                {translate("nevora.resultado.importe_aceptado")}
              </Label>
              <Input
                id="importe_aceptado"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={importe}
                onChange={(e) => setImporte(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                {translate("nevora.resultado.importe_aceptado_ayuda")}
              </p>
            </div>
          ) : null}

          {pide.includes("fechaReunion") ? (
            <div className="space-y-2">
              <Label htmlFor="fecha_reunion">
                {translate("nevora.resultado.fecha_reunion")}
              </Label>
              {ajustes.agenda.propia ? (
                <>
                  <SelectorHuecos valor={hueco} cambiar={setHueco} />
                  <ReservaConsultoria
                    fechaReunion={hueco ? hueco.slice(0, 16) : ""}
                    sinEnlace
                  />
                </>
              ) : (
                <>
                  <Input
                    id="fecha_reunion"
                    type="datetime-local"
                    value={fechaReunion}
                    onChange={(e) => setFechaReunion(e.target.value)}
                    required
                  />
                  <ReservaConsultoria fechaReunion={fechaReunion} />
                </>
              )}
              {/* 39: confirma la cita quien hace la consultoría; el setter se lo anuncia al cliente. */}
              <p
                className="text-xs text-muted-foreground"
                data-testid="aviso-consultor"
              >
                {translate("nevora.resultado.aviso_consultor")}
              </p>
            </div>
          ) : null}

          {pideEmail ? (
            <div className="space-y-2">
              <Label htmlFor="email_reunion">
                {translate("nevora.resultado.email_reunion")}
              </Label>
              <Input
                id="email_reunion"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                {translate("nevora.resultado.email_reunion_ayuda")}
              </p>
            </div>
          ) : null}

          {pide.includes("fechaSiguientePaso") ||
          pide.includes("pasoOpcional") ? (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="fecha_siguiente_paso">
                  {translate("nevora.resultado.fecha_siguiente_paso")}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="fecha_siguiente_paso"
                    type="date"
                    value={fechaSiguientePaso}
                    onChange={(e) => setFechaSiguientePaso(e.target.value)}
                    required={pide.includes("fechaSiguientePaso")}
                  />
                  {"hora_siguiente_paso" in negocio ? (
                    <Input
                      id="hora_siguiente_paso"
                      type="time"
                      className="w-32"
                      aria-label={translate(
                        "nevora.resultado.hora_siguiente_paso",
                      )}
                      value={horaSiguientePaso}
                      onChange={(e) => setHoraSiguientePaso(e.target.value)}
                    />
                  ) : null}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="siguiente_paso">
                  {translate("nevora.resultado.siguiente_paso")}
                </Label>
                <Input
                  id="siguiente_paso"
                  value={siguientePaso}
                  onChange={(e) => setSiguientePaso(e.target.value)}
                  placeholder={translate(
                    "nevora.resultado.siguiente_paso_ayuda",
                  )}
                />
              </div>
            </div>
          ) : null}

          {pide.includes("fechaRespuesta") ? (
            <div className="space-y-2">
              <Label htmlFor="fecha_respuesta">
                {translate("nevora.resultado.fecha_respuesta")}
              </Label>
              <Input
                id="fecha_respuesta"
                type="date"
                value={fechaSiguientePaso}
                onChange={(e) => setFechaSiguientePaso(e.target.value)}
                required
              />
            </div>
          ) : null}

          {pide.includes("fechaVuelta") ? (
            <div className="space-y-2">
              <Label htmlFor="fecha_vuelta">
                {translate("nevora.resultado.fecha_vuelta")}
              </Label>
              <Input
                id="fecha_vuelta"
                type="date"
                value={fechaVuelta}
                onChange={(e) => setFechaVuelta(e.target.value)}
                required
              />
            </div>
          ) : null}

          {pide.includes("motivo") ? (
            <fieldset className="space-y-2">
              <legend className="etiqueta mb-2">
                {translate("nevora.resultado.motivo")}
              </legend>
              <div className="flex flex-wrap gap-2">
                {motivosPara(ajustes, negocio.etapa).map((m) => (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={motivo === m}
                    onClick={() => setMotivo(m)}
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-sm transition-colors",
                      motivo === m
                        ? "border-foreground bg-accent font-medium"
                        : "border-border bg-card hover:bg-muted",
                    )}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </fieldset>
          ) : null}

          {pide.includes("noLlamar") ? (
            <label className="flex items-center gap-3 rounded-lg border px-4 py-3 cursor-pointer">
              <Checkbox
                checked={noLlamar}
                onCheckedChange={(v) => setNoLlamar(v === true)}
              />
              <span>{translate("nevora.resultado.no_llamar")}</span>
            </label>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="nota">
              {translate(
                pide.includes("cambios")
                  ? "nevora.resultado.cambios"
                  : "nevora.resultado.nota",
              )}
            </Label>
            <Textarea
              id="nota"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder={translate(
                pide.includes("cambios")
                  ? "nevora.resultado.cambios_ayuda"
                  : "nevora.resultado.nota_ayuda",
              )}
              rows={pide.includes("cambios") ? 3 : 2}
              required={pide.includes("cambios")}
            />
          </div>

          <button
            type="button"
            className="flex items-center gap-1 text-sm text-muted-foreground"
            onClick={() => setDetalles((v) => !v)}
          >
            <ChevronDown
              className={cn(
                "size-4 transition-transform",
                detalles && "rotate-180",
              )}
            />
            {translate("nevora.resultado.detalles")}
          </button>
          {detalles ? (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="reaccion">
                  {translate("nevora.resultado.reaccion")}
                </Label>
                <Input
                  id="reaccion"
                  value={reaccion}
                  onChange={(e) => setReaccion(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="objecion">
                  {translate("nevora.resultado.objecion")}
                </Label>
                <Input
                  id="objecion"
                  value={conMotivo ? motivo : objecion}
                  disabled={conMotivo}
                  onChange={(e) => setObjecion(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fecha_contacto">
                  {translate("nevora.resultado.fecha_contacto")}
                </Label>
                <Input
                  id="fecha_contacto"
                  type="datetime-local"
                  value={fechaContacto}
                  onChange={(e) => setFechaContacto(e.target.value)}
                />
              </div>
            </div>
          ) : null}
        </div>

        <SheetFooter className="border-t p-4">
          <Button
            type="button"
            className="w-full h-12 text-base"
            disabled={(modo !== "anotar" && !resultado) || guardando}
            onClick={guardar}
          >
            {guardando ? (
              <>
                <Loader2 className="size-4 animate-spin mr-2" />
                {translate("nevora.resultado.guardando")}
              </>
            ) : (
              translate("nevora.resultado.guardar")
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
